import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Nav } from 'react-bootstrap';
import { PageLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { getContrastColor, COLOR_DANGER, COLOR_WARNING, COLOR_SUCCESS } from '../../shared/colorUtils';
import { useQuery } from '@tanstack/react-query';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import StatsCard from '../../components/ui/StatsCard';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/feedback/EmptyState';

const TransportStatistics = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  const { data, isLoading: loading, error: queryError, refetch } = useQuery({
    queryKey: ['transport-statistics'],
    queryFn: async () => {
      const [statsResponse, techChecksResponse] = await Promise.all([
        api.get('transport/api/vehicles/statistics/'),
        api.get('transport/api/vehicles/technical_checks/'),
      ]);
      return { statistics: statsResponse, technicalChecks: techChecksResponse };
    },
  });

  const statistics = data?.statistics || null;
  const technicalChecks = data?.technicalChecks || null;
  const error = queryError?.message || null;

  const renderOverviewTab = () => {
    if (!statistics) return null;

    return (
      <div className="d-flex flex-column gap-3">
        <div className="app-stat-grid">
          <StatsCard title={t('transport.statistics.totalVehicles')} value={statistics.total_vehicles} icon="truck" colorClass="primary" />
          <StatsCard title={t('transport.statistics.availableVehicles')} value={statistics.available_vehicles} icon="check" colorClass="success" />
          <StatsCard title={t('transport.statistics.unavailableVehicles')} value={statistics.unavailable_vehicles} icon="x" colorClass="danger" />
          <StatsCard title={t('transport.statistics.availabilityRate')} value={`${statistics.availability_percentage}%`} icon="chart-donut" colorClass="info" />
        </div>

        <PageSection title={t('transport.statistics.categoryBreakdown')}>
          {statistics.category_statistics && statistics.category_statistics.length > 0 ? (
            <div className="row">
              {statistics.category_statistics.map((category, index) => (
                <div key={index} className="col-md-6 col-lg-4 mb-3">
                  <div className="app-stat-card h-100">
                    <div className="card-body text-center">
                      <div className="mb-2">
                        <StatusBadge className="badge-lg" color={category.category_color} label={category.category_name} />
                      </div>
                      <div className="h1 m-0">{category.total_count}</div>
                      <div className="text-muted">
                        {t('transport.statistics.available')}: {category.available_count} | {t('transport.statistics.unavailable')}: {category.unavailable_count}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title={t('transport.statistics.noCategoryData')}
              message={t('transport.statistics.noCategoryDataMessage', 'Pro kategorie vozidel zatím nejsou k dispozici žádná souhrnná data.')}
            />
          )}
        </PageSection>
      </div>
    );
  };

  const renderTechnicalChecksTab = () => {
    if (!statistics?.technical_check_statistics || !technicalChecks) return null;

    const techStats = statistics.technical_check_statistics;

    return (
      <div className="d-flex flex-column gap-3">
        <div className="app-stat-grid">
          <StatsCard title={t('transport.statistics.overdueChecks')} value={techStats.overdue_count} icon="alert-triangle" colorClass="danger" />
          <StatsCard title={t('transport.statistics.dueSoonChecks')} value={techStats.due_soon_count} icon="clock-hour-4" colorClass="warning" />
          <StatsCard title={t('transport.statistics.noDateSet')} value={techStats.no_date_count} icon="calendar-off" colorClass="secondary" />
          <StatsCard title={t('transport.statistics.complianceRate')} value={`${techStats.compliance_percentage}%`} icon="shield-check" colorClass="success" />
        </div>

        {technicalChecks.overdue && technicalChecks.overdue.length > 0 && (
          <PageSection title={`${t('transport.statistics.overdueVehicles')} (${technicalChecks.overdue.length})`}>
            <div className="table-responsive">
              <table className="table app-table mb-0">
                <thead>
                  <tr>
                    <th>{t('transport.fields.name')}</th>
                    <th>{t('transport.fields.licencePlate')}</th>
                    <th>{t('transport.fields.category')}</th>
                    <th>{t('transport.fields.nextTechnicalCheck')}</th>
                    <th>{t('transport.statistics.daysOverdue')}</th>
                  </tr>
                </thead>
                <tbody>
                  {technicalChecks.overdue.map((vehicle) => (
                    <tr key={vehicle.id}>
                      <td className="fw-bold">{vehicle.name}</td>
                      <td>{vehicle.licence_plate || '-'}</td>
                      <td>
                        {vehicle.category_name && (
                          <StatusBadge color={vehicle.category_color || '#6c757d'} label={vehicle.category_name} />
                        )}
                      </td>
                      <td>{new Date(vehicle.next_technical_check_date).toLocaleDateString()}</td>
                      <td>
                        <StatusBadge color={COLOR_DANGER} label={`${Math.abs(vehicle.days_until_technical_check)} ${t('transport.days')}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </PageSection>
        )}

        {technicalChecks.due_soon && technicalChecks.due_soon.length > 0 && (
          <PageSection title={`${t('transport.statistics.dueSoonVehicles')} (${technicalChecks.due_soon.length})`}>
            <div className="table-responsive">
              <table className="table app-table mb-0">
                <thead>
                  <tr>
                    <th>{t('transport.fields.name')}</th>
                    <th>{t('transport.fields.licencePlate')}</th>
                    <th>{t('transport.fields.category')}</th>
                    <th>{t('transport.fields.nextTechnicalCheck')}</th>
                    <th>{t('transport.statistics.daysRemaining')}</th>
                  </tr>
                </thead>
                <tbody>
                  {technicalChecks.due_soon.map((vehicle) => (
                    <tr key={vehicle.id}>
                      <td className="fw-bold">{vehicle.name}</td>
                      <td>{vehicle.licence_plate || '-'}</td>
                      <td>
                        {vehicle.category_name && (
                          <StatusBadge color={vehicle.category_color || '#6c757d'} label={vehicle.category_name} />
                        )}
                      </td>
                      <td>{new Date(vehicle.next_technical_check_date).toLocaleDateString()}</td>
                      <td>
                        <StatusBadge color={COLOR_WARNING} label={`${vehicle.days_until_technical_check} ${t('transport.days')}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </PageSection>
        )}

        {statistics.upcoming_technical_checks && statistics.upcoming_technical_checks.length > 0 ? (
          <PageSection title={`${t('transport.statistics.upcomingChecks')} (60 ${t('transport.days')})`}>
            <div className="table-responsive">
              <table className="table app-table mb-0">
                <thead>
                  <tr>
                    <th>{t('transport.fields.name')}</th>
                    <th>{t('transport.fields.licencePlate')}</th>
                    <th>{t('transport.fields.category')}</th>
                    <th>{t('transport.fields.nextTechnicalCheck')}</th>
                    <th>{t('transport.statistics.daysRemaining')}</th>
                    <th>{t('transport.technicalCheck.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {statistics.upcoming_technical_checks.map((vehicle) => (
                    <tr key={vehicle.id}>
                      <td className="fw-bold">{vehicle.name}</td>
                      <td>{vehicle.licence_plate || '-'}</td>
                      <td>
                        {vehicle.category_name && (
                          <StatusBadge color={vehicle.category_color || '#6c757d'} label={vehicle.category_name} />
                        )}
                      </td>
                      <td>{new Date(vehicle.next_technical_check_date).toLocaleDateString()}</td>
                      <td>{vehicle.days_until_technical_check} {t('transport.days')}</td>
                      <td>
                        {vehicle.technical_check_status === 'due_soon' ? (
                          <StatusBadge color={COLOR_WARNING} label={t('transport.technicalCheck.dueSoon')} />
                        ) : (
                          <StatusBadge color={COLOR_SUCCESS} label={t('transport.technicalCheck.ok')} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </PageSection>
        ) : (
          <PageSection>
            <EmptyState
              title={t('transport.statistics.noUpcomingChecks', 'Žádné blížící se technické kontroly')}
              message={t('transport.statistics.noUpcomingChecksMessage', 'Momentálně nejsou evidovány žádné technické kontroly v nadcházejících 60 dnech.')}
            />
          </PageSection>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <PageShell pretitle={t('transport.title')} title={t('transport.statistics.title')}>
        <PageSection>
          <PageLoader height="200px" />
        </PageSection>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell pretitle={t('transport.title')} title={t('transport.statistics.title')}>
        <PageSection>
          <Alert variant="danger" className="app-inline-alert mb-0">{error}</Alert>
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('transport.title')}
      title={t('transport.statistics.title')}
      description={t('transport.statistics.subtitle')}
      actions={
        <button type="button" className="btn btn-outline-primary" onClick={refetch} disabled={loading}>
          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/>
            <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/>
          </svg>
          {t('common.refresh')}
        </button>
      }
    >
      <div className="card app-surface">
        <div className="card-body p-3">
          <Nav className="app-tabnav">
            <Nav.Item>
              <Nav.Link as="button" type="button" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                {t('transport.statistics.tabs.overview')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link as="button" type="button" active={activeTab === 'technical'} onClick={() => setActiveTab('technical')}>
                {t('transport.statistics.tabs.technicalChecks')}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div className="pt-3">
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'technical' && renderTechnicalChecksTab()}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default TransportStatistics;
